import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, firstValueFrom, from, switchMap, throwError, type Observable } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * 토큰 미주입 + 401 시 refresh 제외 — 공개 엔드포인트 (규장각 2872f27d 3단 분기).
 *
 * `/auth/extend`가 여기 있는 이유: 연장을 누르는 시점은 만료가 임박한 시점이라 access가
 * 이미 죽어 있을 수 있다. 서버도 같은 이유로 `@Public`이고 인증은 body의 refresh token으로
 * 한다. 여기서 빼면 401 → refresh 회전 → body의 토큰이 구토큰이 되어 재시도가 거부된다.
 *
 * `/auth/find-password`는 비로그인 화면의 공개 요청이다(2026-09-15). 토큰이 남아 있는 상태에서
 * 불릴 일은 없지만, 같은 공개 경로 규칙(토큰 미주입·401에 refresh 없음)에 둔다.
 */
const BYPASS_URLS = ['/auth/login', '/auth/refresh', '/auth/extend', '/auth/find-password'];

/**
 * 토큰 주입 O + 401 시 refresh 제외 — logout이 refresh 경로를 타면
 * rotation 때문에 body의 refreshToken이 구토큰이 되어 새 세션이 서버에 누수된다.
 */
const NO_REFRESH_URLS = ['/auth/logout'];

/**
 * 공유 Promise 1회 갱신 (규장각 2872f27d §6).
 * BehaviorSubject 큐잉은 401 요청이 구독 해제되면 refresh가 체인째 취소되어
 * isRefreshing이 영구 true로 남는 결함이 있다 — Promise는 반드시 완주한다.
 */
let refreshPromise: Promise<string | null> | null = null;

function refreshOnce(auth: AuthService): Promise<string | null> {
  refreshPromise ??= (async () => {
    let sentRefreshToken: string | null = null;
    try {
      auth.syncFromStorage(); // 다른 탭이 먼저 rotation했을 수 있음 — 최신 토큰 재독
      sentRefreshToken = auth.refreshToken();
      if (sentRefreshToken === null) {
        auth.expireSession();
        return null;
      }
      const sentAt = Date.now();
      const tokens = await firstValueFrom(auth.requestRefresh(sentRefreshToken));
      auth.setTokens(tokens.accessToken, tokens.refreshToken);
      // 회전은 세션 만료 시각을 **뒤로 민다**(유휴 기준). 여기서 반영하지 않으면 회전을 탄
      // 세션의 헤더 카운트다운이 옛 값에 머문다 — syncFromStorage가 지운 값을 되돌릴 통로도
      // 여기뿐이다
      auth.setSessionExpiry(tokens, sentAt);
      auth.applyRefreshedPermissions(tokens.permissions);
      return tokens.accessToken;
    } catch (refreshError: unknown) {
      // 멀티탭 방어: 우리 refresh가 나가 있는 동안 다른 탭이 먼저 rotation했으면
      // 구토큰이라 거부된다 — storage 재독 후 토큰이 바뀌어 있으면 그 세션을 채택.
      auth.syncFromStorage();
      const latestRefresh = auth.refreshToken();
      const latestAccess = auth.accessToken();
      if (latestRefresh !== null && latestAccess !== null && latestRefresh !== sentRefreshToken) {
        return latestAccess;
      }
      // 서버가 토큰을 거부한 401/403만 세션 파기. 네트워크 순단·429·5xx는 세션 유지
      // (429를 토큰 거부로 취급하면 레이트리밋에 걸린 정상 사용자가 강제 로그아웃된다).
      const status = refreshError instanceof HttpErrorResponse ? refreshError.status : null;
      if (status === 401 || status === 403) {
        // 서버가 먼저 자른 경우다 — 클라 시계가 느리거나 절전 복귀·백그라운드 탭 스로틀링으로
        // 프론트 카운트다운이 0에 닿기 전에 여기로 온다. 사유를 붙여야 로그인 화면이
        // "자동 로그아웃되었습니다"를 안내한다 (리뷰 W1)
        auth.expireSession();
      }
      return null;
    } finally {
      refreshPromise = null; // 성공·실패 무관 초기화 — 다음 401은 새 refresh
    }
  })();
  return refreshPromise;
}

/**
 * 조작 기반 세션 갱신 — 회전 1회로 서버의 만료 시각을 `지금 + REFRESH_TTL`로 민다.
 * (`SessionActivityService`가 스로틀을 걸어 부른다)
 *
 * 401 처리와 **같은 단일비행을 공유**하는 것이 핵심이다. 따로 두면 갱신과 401 회전이 같은
 * refresh 행을 동시에 회전시켜 두 세션이 발급되고, 나중에 도착한 응답이 앞선 것을 덮는다.
 * 여기 얹으면 뒤에 온 쪽이 앞선 요청의 결과를 그대로 받는다.
 */
export function renewSession(auth: AuthService): Promise<string | null> {
  return refreshOnce(auth);
}

/**
 * 지금 나가 있는 회전 — 없으면 null.
 *
 * [연장] 버튼이 보내기 직전에 확인한다: 회전 중인 토큰으로 연장을 보내면 서버가 이미 소비된
 * 행으로 보고 거부하거나, grace window 안이라 **또 하나의 세션**을 발급한다.
 * Promise를 늘 돌려주지 않고 null을 구분하는 이유는, 기다릴 것이 없을 때 호출부가 마이크로태스크
 * 하나도 소모하지 않고 그대로 요청을 보내게 하기 위해서다.
 */
export function pendingRefresh(): Promise<string | null> | null {
  return refreshPromise;
}

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  error: HttpErrorResponse,
): Observable<HttpEvent<unknown>> {
  return from(refreshOnce(auth)).pipe(
    switchMap((token) => {
      if (token === null) {
        return throwError(() => error); // 원본 401 전파 — 소비자가 에러 컨텍스트 유지
      }
      return next(withBearer(req, token)); // 재시도가 다시 401이면 그대로 전파 (재귀 없음)
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const token = auth.accessToken();

  const isBypass = BYPASS_URLS.some((url) => req.url.includes(url));
  const isNoRefresh = NO_REFRESH_URLS.some((url) => req.url.includes(url));

  const authReq = token !== null && !isBypass ? withBearer(req, token) : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !isBypass &&
        !isNoRefresh
      ) {
        return handle401(authReq, next, auth, error);
      }
      return throwError(() => error);
    }),
  );
};
